import _ from 'underscore';

import { BaseCard } from '@bingads-webui-campaign/overview-base-card';
import { helpers } from '@bingads-webui/deterministic-view';
import {
  TopChangesFilterOption,
  getComparisonDateRange,
  fetchTopChanges,
  topChangesDefaultOptions,
  changeEvents,
  allMetrics,
  isInsightsMetric,
  processTopChangesMetric,
  AccountsMode,
  patchMetric,
  getPopDescription,
} from '@bingads-webui-campaign/overview-cards-common';
import { MetricPicker } from '@bingads-webui-campaign/overview-metric-picker';
import TopChangeGrid from './content';
import metricPickerButtonTemplate from './metric-picker-button-template.pug';
import template from './template.pug';

let i18n;
let items;
const layoutBreakPoint = 800;

function getItems() {
  if (!items) {
    items = [{
      displayText: i18n.getString('Dashboard_TopChanges_ToolbarDropdown_All_Changes'),
      action: TopChangesFilterOption.AllChanges,
    }, {
      displayText: i18n.getString('Dashboard_TopChanges_ToolbarDropdown_Top_Gainers'),
      action: TopChangesFilterOption.TopGainers,
    }, {
      displayText: i18n.getString('Dashboard_TopChanges_ToolbarDropdown_Top_Decliners'),
      action: TopChangesFilterOption.TopDecliners,
    }];

    if (this.accountsMode !== AccountsMode.Smart) {
      items.push({
        displayText: i18n.getString('Dashboard_TopChanges_ToolbarDropdown_Insights'),
        action: TopChangesFilterOption.Insights,
      });
    }
  }

  return items;
}

const layoutSize = {
  small: 0,
  large: 1,
};

/* @this TopChanges */
function getLayoutSize() {
  if (this.$el.closest('.card-holder__wrapper').outerWidth() < layoutBreakPoint) {
    return layoutSize.small;
  }
  return layoutSize.large;
}

/* @this TopChanges */
function reLayout() {
  const newLayoutSize = getLayoutSize.call(this);

  if (this.currentLayoutSize === newLayoutSize) {
    return;
  }

  if (newLayoutSize === layoutSize.small) {
    this.$el.addClass('small_layout');
  } else {
    this.$el.removeClass('small_layout');
  }

  this.currentLayoutSize = newLayoutSize;
}

/* @this TopChanges */
function getCardPreference() {
  const { selectedMenuItem, metric } = this.getState();

  return {
    metric,
    selectedMenuItem,
  };
}

/* @this TopChange */
function loadErrorMsg() {
  helpers.updateView(this.getChild('grid-content'), {
    hasError: true,
    loaded: true,
  });
}

/* @this TopChange */
function updateData() {
  const {
    dateRange,
    selectedMenuItem,
    metric,
    filter,
    permissions,
    dismissLastFluctuation,
  } = this.getState();

  if (!dateRange) {
    return;
  }

  const comparisonDateRange = getComparisonDateRange(dateRange);
  const startDate = i18n.formatDate(dateRange.start);
  const endDate = i18n.formatDate(dateRange.end);
  const comparisonStartDate = i18n.formatDate(comparisonDateRange.start);
  const comparisonEndDate = i18n.formatDate(comparisonDateRange.end);
  let currentDateRangeText;
  let comparisonDateRangeText;

  this.popText = getPopDescription(i18n, comparisonDateRange);
  this.popTextAriaLabel = (i18n.getString('Click to learn more about {{item}}') || '').replace('{{item}}', this.popText);
  if (startDate === endDate) {
    currentDateRangeText = startDate;
    comparisonDateRangeText = comparisonStartDate;
  } else {
    currentDateRangeText = `${startDate} - ${endDate}`;
    comparisonDateRangeText = `${comparisonStartDate} - ${comparisonEndDate}`;
  }

  const {
    instrumentation, constants, jsdata, dateUtil, cacheManager,
  } = this.deps;

  this.blockCard();
  this.willFetchData();

  fetchTopChanges({
    entitySet: this.entitySet,
    dateRange,
    filter,
    comparisonDateRange,
    selectedMenuItem,
    metric,
    jsdata,
    getCardsConfig: this.getCardsConfig,
    dateUtil,
    fetchSingleTile: this.fetchSingleTile,
    isDevice: true,
    permissions,
    disableCache: dismissLastFluctuation,
    cacheManager,
    accountsMode: this.accountsMode,
    dfmCacheKey: this.typeId,
  }).finally(() => {
    this.unblockCard();
    this.setState({ dismissLastFluctuation: false });
    const firstRow = this.$('.top-change-grid .top-change-entity-name.top-change-grid-row-1 a');
    if (firstRow.length > 0) {
      firstRow.focus();
    } else {
      this.$('.view-all-clickLink').focus();
    }
  }).then((data) => {
    if (this.removed) {
      return;
    }

    if (!data || (data.status !== null && data.status !== undefined)) {
      loadErrorMsg.call(this);
      // If we have error status. Those are already logged in ajax stub.
      // Add a trace log.
      instrumentation.logMessage([constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, 'top-changes-card', JSON.stringify(data));
      return;
    }

    let dataError = false;

    _.each(data, (entity) => {
      if (!entity || !entity.PerformanceMetricsChange) {
        dataError = true;
      }
      if (entity.AvailableInsightTypeIds && entity.AvailableInsightTypeIds.length) {
        const fromScenario = entity.CampaignType ? 'CampaignsOverview' : 'AccountsOverView';
        instrumentation.logMessage(
          [constants.LOGCATEGORY.TRACE], constants.LOGLEVEL.INFO, 'top-changes-card',
          `Fluctuations Rendered. FromScenario: ${fromScenario}, FilterOption: ${selectedMenuItem}, Metric: ${metric}, TypeIds: ${entity.AvailableInsightTypeIds.toString()}, id: ${entity.Id}}.`
        );
      }
    });

    if (dataError) {
      loadErrorMsg.call(this);
      instrumentation.logMessage([constants.LOGCATEGORY.ERROR], constants.LOGLEVEL.INFO, 'top-changes-card', JSON.stringify(data));
      return;
    }

    let gridData = data;

    if (selectedMenuItem === TopChangesFilterOption.TopDecliners) {
      gridData = _.filter(gridData, entity => entity.PerformanceMetricsChange[metric] < 0);
    } else if (selectedMenuItem === TopChangesFilterOption.TopGainers) {
      gridData = _.filter(gridData, entity => entity.PerformanceMetricsChange[metric] > 0);
    } else if (selectedMenuItem === TopChangesFilterOption.Insights) {
      gridData = isInsightsMetric(metric) ? _.filter(
        gridData,
        entity => !_.isEmpty(entity.AvailableInsightTypeIds)
      ) : [];
    }

    helpers.updateView(this.getChild('grid-content'), {
      loaded: true,
      hasError: false,
      data: gridData,
      metric,
      entitySet: this.entitySet,
      currentDateRangeText,
      comparisonDateRangeText,
      dateRange,
    });
  }).catch((response) => {
    // no need to log ajax error here.
    if (response.status === undefined) {
      instrumentation.logMessage([constants.LOGCATEGORY.ERROR], constants.LOGLEVEL.INFO, 'top-changes-card', response.stack);
    }
    loadErrorMsg.call(this);
  });
}

/* @this TopChange */
function updateMetricPicker() {
  const { metric, permissions } = this.getState();

  helpers.updateView(this.metricPickerChild, {
    metric,
    metrics: _.difference(allMetrics(permissions, this.accountsMode), [metric]),
  });
}

/* @this TopChange */
function navigateToEntityGrid() {
  const {
    legacyRouter,
    state,
    instrumentation,
    constants,
  } = this.deps;

  instrumentation.logMessage(
    [constants.LOGCATEGORY.TRACE],
    constants.LOGLEVEL.INFO,
    'top-changes-card',
    `${this.navigationLevel} level view all clicked`
  );

  if (this.navigationLevel === 'account') {
    legacyRouter.navigateToRoute('campaign');
  } else if (this.navigationLevel === 'customer') {
    legacyRouter.navigateToRoute('accounts_performance', { accountMode: this.accountsMode });
  } else {
    legacyRouter.navigateToRoute('campaign_adgroup', {
      // eslint-disable-next-line camelcase
      campaign_id: state.get('campaign_id'),
    });
  }
}

/* @this TopChange */
function updatePopupPosition(evt) {
  const $target = this.$(evt.currentTarget);
  const hoverContent = $target.find('.hover-content');

  if (hoverContent.attr('style')) {
    hoverContent.attr('style', '');
  }

  const $viewport = $target.closest('.top-change-grid-container');
  const scrollTop = $viewport.scrollTop();

  if (scrollTop <= 0) {
    return;
  }

  if ($target.offset().top - $viewport.offset().top > 50) {
    hoverContent.css({
      top: 'auto',
      bottom: `${scrollTop}px`,
    });
  } else {
    hoverContent.css({
      top: `${50 - scrollTop}px`,
      bottom: 'auto',
    });
  }
}

/* @this TopChange */
function setupScrollBarCover() {
  const viewport = this.$('.top-change-grid-container');
  const viewportWidth = viewport.width();
  const grid = this.$('.top-change-grid');
  const gridWidth = grid.width();

  if (viewportWidth && gridWidth && (viewportWidth > gridWidth)) {
    const scrollBarWidth = viewportWidth - gridWidth;
    const scrollBarHeight = viewport.height();

    this.$('.scroll-bar-cover', this.$el.closest('.grid-card')).css({
      width: scrollBarWidth,
      height: scrollBarHeight,
    });

    const expand = Math.min(scrollBarWidth, 16);

    viewport.css({ marginRight: -expand });
  }
}

export class TopChangesCard extends BaseCard {
  get className() {
    return 'top-changes-card';
  }

  get template() {
    return () => template({
      popText: this.popText,
      buttonText: this.buttonText,
      isCustomerLevel: this.navigationLevel === 'customer',
      popTextAriaLabel: this.popTextAriaLabel,
    });
  }

  get menuItems() {
    const { selectedMenuItem } = this.getState();

    _.each(getItems.call(this), (item) => {
      if (item.action === selectedMenuItem) {
        // eslint-disable-next-line no-param-reassign
        item.preset = true;
      } else {
        // eslint-disable-next-line no-param-reassign
        item.preset = false;
      }
    });

    return items;
  }

  onMenuAction(menuItem) {
    const { selectedMenuItem } = this.getState();

    if (selectedMenuItem === menuItem) {
      return;
    }

    const {
      instrumentation, constants,
    } = this.deps;

    instrumentation.logMessage(
      [constants.LOGCATEGORY.TRACE],
      constants.LOGLEVEL.INFO,
      'top-changes-card',
      `${this.navigationLevel} level new pop type picked.`
    );

    this.setState({
      selectedMenuItem: menuItem,
    });
  }

  initialize({
    entitySet,
    metric,
    selectedMenuItem,
    dateRange,
    filter,
    navigationLevel,
    perfMarker,
    name,
    sharedOptions,
    deps,
    fetchSingleTile = false,
    preexecuteFlag = false,
    accountsMode,
    scope,
    dismissLastFluctuation = false,
    typeId,
  }) {
    this.getCardsConfig = sharedOptions.getCardsConfig;
    this.fetchSingleTile = fetchSingleTile;
    this.accountsMode = accountsMode;
    this.deps = deps;
    this.typeId = typeId;
    this.initPerfMarker({ perfMarker, name });
    // eslint-disable-next-line prefer-destructuring
    i18n = deps.i18n;
    this.setChild('grid-content', new TopChangeGrid({
      deps: {
        ...deps,
        context: {
          ...deps.context,
          scope,
        },
      },
      perfMarker: this.perfMarker,
    }));

    const permissions = deps.config && deps.config.get('Permissions');
    const initialState = _.defaults({
      metric,
      selectedMenuItem,
      dateRange,
      filter,
      permissions,
      dismissLastFluctuation,
    }, topChangesDefaultOptions);

    const patchedMetric = patchMetric({
      permissions,
      metric: initialState.metric,
      accountsMode,
    });
    const metricProcessed = processTopChangesMetric({
      metric: patchedMetric,
      permissions,
      accountsMode,
    });

    if (metricProcessed !== initialState.metric) {
      _.extend(initialState, { metric: metricProcessed });

      this.setState(initialState);
      _.defer(() => {
        this.trigger('storeCardPreference', getCardPreference.call(this));
      });
    } else {
      this.setState(initialState);
    }

    this.entitySet = entitySet;
    const { metric: initialMetric } = this.getState();

    this.metricPickerChild = helpers.ensureChildView(this, 'metric-picker', MetricPicker, {
      buttonTemplate: metricPickerButtonTemplate,
      metric: initialMetric,
      metrics: _.difference(allMetrics(permissions, this.accountsMode), [initialMetric]),
      metricFormatter: this.deps.metricFormatter,
      s$i18n: this.deps.s$i18n,
      i18n,
      accountsMode,
      deps,
    }, {
      pick: ({ metric: newMetric }) => {
        const {
          instrumentation, constants,
        } = this.deps;

        instrumentation.logMessage(
          [constants.LOGCATEGORY.TRACE],
          constants.LOGLEVEL.INFO,
          'top-changes-card',
          `${this.navigationLevel} level new metric picked.`
        );
        this.setState({ metric: newMetric });
        updateMetricPicker.call(this);
      },
    });
    this.navigationLevel = navigationLevel;

    switch (navigationLevel) {
      case 'account':
        this.buttonText = i18n.getString('Dashboard_TopCampaigns_View_All');
        break;
      case 'customer':
        this.buttonText = i18n.getString('Dashboard_TopAccounts_View_All');
        break;
      case 'campaign':
        this.buttonText = i18n.getString('Dashboard_TopAdGroups_View_All');
        break;
      default:
    }

    const updateDataFunc = preexecuteFlag ? () => {
      helpers.updateView(this.getChild('grid-content'), {
        loaded: true,
      });
    } : updateData;
    const comparisonDateRange = getComparisonDateRange(dateRange);

    this.popText = getPopDescription(i18n, comparisonDateRange);
    this.popTextAriaLabel = (i18n.getString('Click to learn more about {{item}}') || '').replace('{{item}}', this.popText);
    updateMetricPicker.call(this);
    updateDataFunc.call(this);

    this.listenTo(this.model, changeEvents('dateRange', 'filter'), () => {
      this.fetchSingleTile = false;
      updateDataFunc.call(this);
    });

    this.listenTo(this.model, changeEvents('selectedMenuItem', 'metric'), () => {
      this.fetchSingleTile = true;
      updateDataFunc.call(this);
    });

    this.listenTo(this.model, changeEvents('dismissLastFluctuation'), () => {
      this.fetchSingleTile = true;
      updateData.call(this);
    });

    this.listenTo(this.model, changeEvents('metric'), () => {
      updateMetricPicker.call(this);
    });

    this.listenTo(this.model, changeEvents('selectedMenuItem'), () => {
      this.trigger('change:menuItems', {
        value: this.menuItems,
        options: { replace: true },
      });
    });

    this.listenTo(this.model, changeEvents('selectedMenuItem', 'metric'), () => {
      this.trigger('storeCardPreference', getCardPreference.call(this));
    });

    this.onResize(() => {
      reLayout.call(this);
      setupScrollBarCover.call(this);
    });

    this.on('removed', () => {
      this.removed = true;
    });

    this.listenTo(this.model, changeEvents('size'), () => {
      setupScrollBarCover.call(this);
    });

    this.getChild('grid-content').on('rendered', () => {
      if (this.deps.onMounted) {
        this.deps.onMounted.schedule(() => setupScrollBarCover.call(this));
      } else {
        setupScrollBarCover.call(this);
      }
    });
  }

  get events() {
    return {
      'mouseover .graph-hover-target,.pop-hover-target': updatePopupPosition.bind(this),
      'click .view-all-clickLink': navigateToEntityGrid.bind(this),
    };
  }

  renderTrunk() {
    this.currentLayoutSize = undefined;
    super.renderTrunk();
    _.defer(() => {
      reLayout.call(this);
    });
  }
}
